import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { emailSchema } from "../../../utils/findIdValidationSchema";
import {
    useFindIdByEmailMutation,
    useVerifyEmailMutation,
} from "../../../api/FindIdAPI";
import { useNavigate } from "react-router-dom";

const EmailForm = () => {
    // 아이디 찾기 요청 상태 관리 (idle, pending, success, failure)
    const [isLoading, setIsLoading] = useState(false);

    // 인증번호 요청 상태 관리 (idle, sending, success, failure)
    const [verificationStatus, setVerificationStatus] = useState("idle");

    // 인증번호 요청 결과 메시지 상태 관리
    const [verificationMessage, setVerificationMessage] = useState("");

    // 남은 시간 (초 단위) 상태 관리
    const [remainingTime, setRemainingTime] = useState(180); // 3 minutes in seconds

    // 휴대폰 인증번호 요청 mutation
    const verifyEmailMutation = useVerifyEmailMutation();
    const findIdMutation = useFindIdByEmailMutation();

    const navigate = useNavigate();

    const {
        register,
        handleSubmit,
        formState: { errors },
        watch,
    } = useForm({
        resolver: zodResolver(emailSchema),
        mode: "onChange",
    });

    const name = watch("name");
    const email = watch("email");
    const authNumber = watch("authNumber");

    /* 타이머 설정 함수 */
    useEffect(() => {
        let timer;
        if (verificationStatus === "sent" && remainingTime > 0) {
            timer = setInterval(() => {
                setRemainingTime((prevTime) => prevTime - 1);
            }, 1000);
        } else if (remainingTime === 0) {
            setVerificationStatus("idle");

            // 타이머 종료 메세지 설정
            setVerificationMessage(
                "인증번호 입력 시간이 초과되었습니다. 다시 시도해주세요."
            );
        }
        return () => clearInterval(timer);
    }, [verificationStatus, remainingTime]);

    /* 휴대폰 인증번호 요청 함수 */
    const handleEmailVerification = async () => {
        setVerificationStatus("sending");
        setVerificationMessage("");
        // setError(null);

        try {
            await verifyEmailMutation.mutateAsync({ name, email });
            setVerificationStatus("sent");
            setVerificationMessage(
                `인증번호가 발송되었습니다. 이메일을 확인해주세요.`
            );
            setRemainingTime(180); // 타이머 3분 설정
        } catch (error) {
            // 가입되지 않은 회원일 경우
            if (error.message === "가입되지 않은 회원입니다.") {
                setVerificationMessage("가입되지 않은 회원입니다.");
                setVerificationStatus("idle");
                return;
            }

            // 전화번호 인증 요청 실패 시
            setVerificationMessage("이메일 인증 요청에 실패했습니다.");
            // setVerificationMessage(error.message);
            setVerificationStatus("idle");
        }
    };

    /* 시간 포맷 함수 */
    const formatTime = (seconds) => {
        const minutes = Math.floor(seconds / 60);
        const remainingSeconds = seconds % 60;
        return `${minutes}:${
            remainingSeconds < 10 ? "0" : ""
        }${remainingSeconds}`;
    };

    /* 아이디 찾기 submit 함수 */
    const onSubmit = async (formData) => {
        // 아이디 찾기 버튼 클릭 시 로딩 상태로 변경
        setIsLoading(true);

        // 아이디 찾기 요청
        try {
            const signupData = {
                name: formData.name,
                email: formData.email,
                authNumber: formData.authNumber,
                timeStamp: Date.now(),
            };

            let result = await findIdMutation.mutateAsync(signupData);
            if (result["message"] === "이메일로 아이디 찾기 성공") {
                alert("아이디 찾기 성공");
                navigate("/find-id/result", { state: { result: result } });
            }
        } catch (error) {
            console.error("ID 찾기 실패:", error);
            throw error;
        } finally {
            // 아이디 찾기 요청 완료 시 로딩 상태 해제
            setIsLoading(false);
        }
    };

    return (
        <div className="flex flex-col items-center justify-center min-h-screen bg-white px-4 mt-[170px]">
            <div className="w-full max-w-md space-y-8">
                <h2 className="text-2xl font-bold mb-4 text-center">
                    이메일로 아이디 찾기
                </h2>
                <form
                    className="mt-8 space-y-4"
                    onSubmit={handleSubmit(onSubmit)}
                >
                    <input
                        {...register("name")}
                        type="text"
                        placeholder="이름을 입력하세요"
                        className="w-full h-[60px] px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    {errors.name && (
                        <p className="mt-1 text-sm text-red-500">
                            {errors.name.message}
                        </p>
                    )}
                    <div className="flex items-center space-x-2 mb-4">
                        <input
                            {...register("email")}
                            type="email"
                            placeholder="이메일을 입력하세요"
                            className="w-full h-[60px] px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                        {errors.email && (
                            <p className="mt-1 text-sm text-red-500">
                                {errors.email.message}
                            </p>
                        )}
                        <button
                            className="px-4 h-[60px] py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 whitespace-nowrap"
                            onClick={handleEmailVerification}
                            disabled={
                                verificationStatus === "sending" || !email
                            }
                        >
                            {verificationStatus === "idle" ||
                            remainingTime === 0
                                ? "인증요청"
                                : verificationStatus === "sending"
                                ? "요청중..."
                                : "재요청"}
                        </button>
                    </div>
                    {verificationMessage && (
                        <p
                            className={`mt-2 text-sm ${
                                verificationMessage.includes("발송되었습니다.")
                                    ? "text-green-500"
                                    : "text-red-500"
                            }`}
                        >
                            {verificationMessage}
                        </p>
                    )}
                    {verificationStatus === "sent" && remainingTime > 0 && (
                        <p className="mt-1 text-sm text-gray-500">
                            남은 시간: {formatTime(remainingTime)}
                        </p>
                    )}
                    {verificationStatus === "sent" && (
                        <div>
                            <input
                                {...register("authNumber")}
                                type="text"
                                placeholder="인증번호 입력"
                                className="w-full h-[60px] px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                            {errors.authNumber && (
                                <p className="mt-1 text-sm text-red-500">
                                    {errors.authNumber.message}
                                </p>
                            )}
                        </div>
                    )}
                    <button
                        className="w-full h-[60px] py-3 px-4 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 mt-6 disabled:opacity-50"
                        disabled={
                            isLoading ||
                            !authNumber ||
                            verificationStatus !== "sent"
                        }
                    >
                        {isLoading ? "처리 중..." : "아이디 찾기"}
                    </button>
                </form>
            </div>
            <div className="flex justify-center mt-[260px]">
                <img
                    src="/images/softcat_new_logo.png"
                    alt=" "
                    className="w-[180px] h-[50px]"
                />
            </div>
        </div>
    );
};

export default EmailForm;
